import { type FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthShell from "../components/AuthShell";
import { HxAlert, HxButton } from "../components/hx";
import { Field, FieldGroup, FieldLabel } from "../components/ui/field";
import { PasswordInput } from "../components/ui/password-input";
import { ApiError, opsFetch } from "../api";
import type { Me } from "../App";

interface Props {
  me: Me;
  onChanged: () => Promise<void> | void;
}

// The template's register form (auth/_components/register-form.tsx): Field
// rows in a FieldGroup. `Field` rather than HxField, whose label takes no
// htmlFor and so would stop naming the field. Record:
// docs/testing/ui-migration/087-password-change.md.
export default function PasswordChange({ me, onChanged }: Props) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError("New password and confirmation don't match.");
      return;
    }

    setBusy(true);
    const wasForced = me.mustChangePassword;
    try {
      await opsFetch("/ops/password/change", {
        method: "POST",
        json: { currentPassword, newPassword },
      });
      await onChanged();
      navigate(wasForced ? "/" : "/profile", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "weak_password") {
          setError(
            err.message ||
              "Password too weak. Use 12+ chars with letters, digits and a symbol.",
          );
        } else if (err.code === "password_breached") {
          setError("That password has appeared in a public breach. Pick a different one.");
        } else if (err.code === "invalid_credentials") {
          setError("Current password is incorrect.");
        } else if (err.code === "not_implemented") {
          setError("Password change not wired up yet (Phase 0 step 4 pending).");
        } else {
          setError("Couldn't change password. Try again.");
        }
      } else {
        setError("Something went wrong. Try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title={me.mustChangePassword ? "Set a new password" : "Change your password"}
      subtitle={
        me.mustChangePassword
          ? "We set a temporary bootstrap password to get you in. Pick a new one now."
          : "Pick a new password."
      }
    >
      <form onSubmit={submit} autoComplete="off" className="flex flex-col gap-4">
        <FieldGroup className="gap-4">
          <Field className="gap-1.5">
            <FieldLabel htmlFor="current">Current password</FieldLabel>
            <PasswordInput
              id="current"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              autoFocus
            />
          </Field>
          <Field className="gap-1.5">
            <FieldLabel htmlFor="new">New password</FieldLabel>
            <PasswordInput
              id="new"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
          </Field>
          <Field className="gap-1.5">
            <FieldLabel htmlFor="confirm">Confirm new password</FieldLabel>
            <PasswordInput
              id="confirm"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </Field>
        </FieldGroup>
        <HxButton variant="primary" type="submit" disabled={busy} className="w-full">
          {busy ? "Updating…" : "Update password"}
        </HxButton>
        {error && (
          <HxAlert aria-live="polite" className="mb-0">
            {error}
          </HxAlert>
        )}
      </form>
    </AuthShell>
  );
}
